import { mapGetters, mapActions } from 'vuex'
import Sidebar from '@/components/Sidebar/Sidebar.vue'

export default {
  name: 'PageSimilarity',
  components: {
    Sidebar
  },
  data () {
    return {
      isLoading: {
        analysis: false,
        clusters: false,
        rejectMerge: false
      },
      clusters: []
    }
  },
  async mounted () {
    await this.loadClusters()
  },
  watch: {
    async $route () {
      await this.loadClusters()
    }
  },
  computed: {
    ...mapGetters([
      'activeContentplan',
      'activeWorkspace'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan?.name ?? ''
    },
    totalMergeOpportunities () {
      return this.clusters.length
    },
    totalPagesToMerge () {
      return this.clusters.reduce((sum, cluster) => {
        return sum + cluster.pages.filter(p => !p.is_winner).length
      }, 0)
    },
    sortedClusters () {
      return [...this.clusters].sort((a, b) => {
        const impA = a.pages.find(p => p.is_winner)?.google_impressions_last || 0
        const impB = b.pages.find(p => p.is_winner)?.google_impressions_last || 0
        return impB - impA
      }).map(cluster => ({
        ...cluster,
        pages: this.sortPagesInCluster(cluster.pages)
      }))
    }
  },
  methods: {
    ...mapActions([
      'pageSimilarityTriggerAnalysis',
      'pageSimilarityGetClusters',
      'pagePut',
      'setSnackbar'
    ]),
    async loadClusters () {
      this.isLoading.clusters = true
      try {
        const response = await this.pageSimilarityGetClusters({
          contentplanId: this.contentplanId
        })
        this.clusters = response.clusters || []
      } catch (error) {
        console.error('Error loading clusters:', error)
      } finally {
        this.isLoading.clusters = false
      }
    },
    async analyzeNow () {
      this.isLoading.analysis = true
      try {
        await this.pageSimilarityTriggerAnalysis({
          contentplanId: this.contentplanId
        })
        await this.setSnackbar({
          open: true,
          text: 'Page similarity analysis started. This may take a few minutes.',
          type: 'success'
        })

        // Poll for results
        setTimeout(async () => {
          await this.loadClusters()
        }, 60000) // Reload after 1 minute
      } catch (error) {
        console.error('Error triggering analysis:', error)
        await this.setSnackbar({
          open: true,
          text: 'Failed to start analysis',
          type: 'error'
        })
      } finally {
        this.isLoading.analysis = false
      }
    },
    async rejectMerge (cluster) {
      this.isLoading.rejectMerge = true
      try {
        const mergePages = cluster.pages.filter(p => !p.is_winner)
        const pageIds = mergePages.map(p => p.id)

        await this.pagePut({
          contentplanId: this.contentplanId,
          action: 'clearMergeRecommendation',
          pageIds
        })

        await this.setSnackbar({
          open: true,
          text: `Rejected merge recommendations for ${mergePages.length} pages`,
          type: 'success'
        })

        await this.loadClusters()
      } catch (error) {
        console.error('Error rejecting merge:', error)
        await this.setSnackbar({
          open: true,
          text: 'Failed to reject merge recommendations',
          type: 'error'
        })
      } finally {
        this.isLoading.rejectMerge = false
      }
    },
    sortPagesInCluster (pages) {
      return [...pages].sort((a, b) => {
        if (a.is_winner) return -1
        if (b.is_winner) return 1
        return (b.similarity_score || 0) - (a.similarity_score || 0)
      })
    },
    formatUrl (url) {
      if (!url) return ''
      return url.length > 80 ? url.substring(0, 80) + '...' : url
    },
    formatNumber (num) {
      if (!num) return '0'
      return num.toLocaleString()
    }
  }
}
